import { useId, type ChangeEvent, type KeyboardEvent, type MouseEvent } from 'react';
import { Check, Minus, Plus } from 'lucide-react';
import type { AvailableSlotHour, BookingSlot } from '@/features/campaigns/types/campaign';
import { formatHourRange } from '@/features/campaigns/utils/campaignUtils';
import {
  SLOT_BAR_CLASS,
  SLOT_CARD_CLASS,
  SLOT_STATUS_LABEL,
  SLOT_STATUS_TEXT,
  getSlotTone,
} from '../scheduleStep.constants';

type SlotCardProps = {
  date: string;
  slot: AvailableSlotHour;
  selected: boolean;
  current: BookingSlot | undefined;
  defaultTotalReps: number;
  onToggle: (date: string, hour: number, maxReps: number) => void;
  onSetReps: (date: string, hour: number, repetitions: number) => void;
};

const PRESETS = [1, 5, 10, 20, 50];

/**
 * Inventory slot with accordion frequency controls.
 * Selecting a card expands an inline play editor — no extra modal step.
 */
export function SlotCard({
  date,
  slot,
  selected,
  current,
  defaultTotalReps,
  onToggle,
  onSetReps,
}: SlotCardProps) {
  const inputId = useId();
  const availableReps = slot.availableRepetitions ?? 0;
  const totalReps = Math.max(1, slot.totalRepetitions ?? defaultTotalReps);
  const max = Math.max(0, availableReps);
  const reps = current?.repetitions ?? 1;
  const availabilityTone = getSlotTone(false, availableReps, totalReps);
  const canSelect = availableReps > 0 || selected;
  const availablePct = Math.min(
    100,
    Math.max(0, Math.round((availableReps / totalReps) * 100)),
  );
  const timeLabel = slot.timeRange || formatHourRange(slot.hour);
  const visiblePresets = PRESETS.filter((p) => p <= max);

  const handleToggle = () => {
    if (!canSelect) return;
    onToggle(date, slot.hour, Math.max(1, max));
  };

  const stopCardToggle = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
  };

  const handleStep = (delta: number) => {
    const next = Math.max(1, Math.min(max, reps + delta));
    onSetReps(date, slot.hour, next);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    if (!Number.isNaN(val)) {
      onSetReps(date, slot.hour, Math.max(1, Math.min(max, val)));
    }
  };

  return (
    <div
      role="button"
      tabIndex={canSelect ? 0 : -1}
      aria-disabled={!canSelect}
      aria-pressed={selected}
      aria-expanded={selected}
      onClick={handleToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      }}
      className={`group relative flex w-full flex-col rounded-xl border p-2.5 text-left transition-[border-color,box-shadow,background-color] duration-200 ease-out select-none ${
        canSelect ? 'cursor-pointer' : 'cursor-not-allowed'
      } ${
        selected ? SLOT_CARD_CLASS.selected : SLOT_CARD_CLASS[availabilityTone]
      }`}
    >
      {/* Always-visible summary */}
      <div className="flex items-start justify-between gap-2">
        <p className="m-0 text-[13px] font-semibold leading-tight text-[#1F2433]">
          {timeLabel}
        </p>
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors duration-200 ${
            selected
              ? 'border-[var(--dash-brand)] bg-[var(--dash-brand)] text-white'
              : 'border-[#D0D4DE] bg-white text-transparent group-hover:border-[var(--dash-brand)]/50'
          }`}
          aria-hidden
        >
          <Check className="h-3 w-3 stroke-[3]" />
        </span>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${SLOT_STATUS_TEXT[availabilityTone]}`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${SLOT_BAR_CLASS[availabilityTone]}`}
          />
          {SLOT_STATUS_LABEL[availabilityTone]}
        </span>
        {canSelect ? (
          <span className="text-[11px] tabular-nums text-[#6B7289]">
            {max.toLocaleString()} left
          </span>
        ) : null}
      </div>

      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-[#EEF0F5]">
        <div
          className={`h-full rounded-full transition-all duration-200 ${SLOT_BAR_CLASS[availabilityTone]}`}
          style={{ width: `${canSelect ? availablePct : 0}%` }}
        />
      </div>

      {!selected ? (
        <p
          className={`m-0 mt-2 text-[11px] font-medium ${
            canSelect
              ? 'text-[var(--dash-brand)] group-hover:underline'
              : 'text-[#9CA3AF]'
          }`}
        >
          {canSelect ? 'Tap to book' : 'Unavailable'}
        </p>
      ) : null}

      {/* Accordion frequency panel — expands only when selected */}
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          selected ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={`border-t border-[var(--dash-brand)]/15 pt-2.5 transition-opacity duration-200 ${
              selected ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={stopCardToggle}
            onKeyDown={stopCardToggle}
          >
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <label
                htmlFor={inputId}
                className="text-[10px] font-bold tracking-wide text-[var(--dash-brand)] uppercase"
              >
                Plays
              </label>
              <span className="text-[10px] text-[#6B7289]">
                Max {max.toLocaleString()}
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={reps <= 1}
                onClick={() => handleStep(-1)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#E6E8EF] bg-white text-[#1F2433] transition hover:bg-[#F8F9FB] disabled:opacity-30"
                aria-label="Decrease plays"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <input
                id={inputId}
                type="number"
                min={1}
                max={max}
                value={reps}
                onChange={handleInputChange}
                className="h-8 min-w-0 flex-1 rounded-lg border border-[var(--dash-brand)]/35 bg-white px-2 text-center text-[12px] font-semibold tabular-nums text-[#1F2433] outline-none focus:ring-2 focus:ring-[var(--dash-brand)]/15 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                aria-label="Plays per hour"
              />
              <button
                type="button"
                disabled={reps >= max}
                onClick={() => handleStep(1)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#E6E8EF] bg-white text-[#1F2433] transition hover:bg-[#F8F9FB] disabled:opacity-30"
                aria-label="Increase plays"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="mt-2 flex flex-wrap gap-1">
              {visiblePresets.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => onSetReps(date, slot.hour, p)}
                  className={`rounded-md px-2 py-1 text-[10px] font-bold transition ${
                    reps === p
                      ? 'bg-[var(--dash-brand)] text-white'
                      : 'border border-[#E6E8EF] bg-white text-[#6B7289] hover:text-[#1F2433]'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => onSetReps(date, slot.hour, Math.max(1, max))}
                className={`rounded-md px-2 py-1 text-[10px] font-bold transition ${
                  reps === max
                    ? 'bg-[var(--dash-brand)] text-white'
                    : 'border border-[#E6E8EF] bg-white text-[#6B7289] hover:text-[#1F2433]'
                }`}
                title={`Use maximum available (${max})`}
              >
                Max ({max})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
